/* global Flickity */
// Variant-specific PDP gallery for snippets/product-images-grid-slider.liquid.
// Liquid renders only the selected variant's media, keeps the rest in <template data-variant-media-pool>
// and prints {variantId: [mediaIds]} in [data-variant-media-map] (rule: snippets/variant-media-ids).
// On variant change the slides and thumbs are remounted from that map, never from filenames or alt text.
class VariantMediaGallery {
  constructor(root, {
    slideSelector = '.product-main-slide',
    thumbItemSelector = '.product__thumb-item'
  } = {}) {
    this.root = root;
    this.main = root.querySelector('[data-product-photos]');
    if (!this.main) return;

    this.slideSelector = slideSelector;
    this.thumbsWrap = root.querySelector('[data-product-thumbs]');
    this.thumbsScroller = this.thumbsWrap ? this.thumbsWrap.querySelector('.product__thumbs--scroller') : null;
    this.thumbItemSelector = thumbItemSelector;

    const mapEl = root.querySelector('[data-variant-media-map]');
    this.variantMedia = mapEl ? JSON.parse(mapEl.textContent) : {};

    // The theme's own product slider starts on these slides first; take them back before reading them
    this.destroyFlickity();

    // Slide and thumb markup by media id: the ones on the page plus the ones waiting in the pools
    this.slidesHTML = this.collectHTML(this.main, slideSelector, root.querySelector('[data-variant-media-pool]'));
    this.thumbsHTML = this.collectHTML(this.thumbsScroller, thumbItemSelector, root.querySelector('[data-variant-thumb-pool]'));
    this.currentIds = Array.from(this.main.querySelectorAll(slideSelector)).map(slide => slide.dataset.mediaId);

    this.installListeners();
    this.mount();
  }

  collectHTML(container, selector, pool) {
    const html = {};
    const items = container ? Array.from(container.querySelectorAll(selector)) : [];
    if (pool) items.push(...pool.content.querySelectorAll(selector));
    items.forEach(item => {
      item.classList.remove('is-selected');
      item.removeAttribute('aria-hidden');
      html[item.dataset.mediaId] = item.outerHTML;
    });
    return html;
  }

  installListeners() {
    document.addEventListener('variant:change', (e) => {
      const variant = e.detail && e.detail.variant;
      if (variant) this.showVariant(variant);
    });

    // Sync thumb click with Flickity slide selection
    if (this.thumbsWrap) {
      this.thumbsWrap.addEventListener('click', (evt) => {
        const item = evt.target.closest(this.thumbItemSelector);
        if (!item || !this.flickity) return;
        evt.preventDefault();
        this.flickity.select(parseInt(item.getAttribute('data-index'), 10), false, true);
      });
    }

    // Keep the slider on the image last viewed in the zoom lightbox
    this.root.addEventListener('photoswipe:afterChange', (evt) => {
      if (this.flickity) this.flickity.select(evt.detail.index, false, true);
    });

    this.isZooming = false;
  }

  // Opens the zoom lightbox (swipeable, pinch to zoom) on the tapped or clicked photo, through its zoom button
  openZoom(evt, slide) {
    if (!slide || this.isZooming || evt.target.closest('button, a, video, model-viewer')) return;
    const zoomBtn = slide.querySelector('.js-photoswipe__zoom');
    if (!zoomBtn) return;
    this.isZooming = true;

    this.main.querySelectorAll(this.slideSelector).forEach((s) => s.classList.remove('is-selected'));
    slide.classList.add('is-selected');

    setTimeout(() => {
      zoomBtn.click();
      setTimeout(() => (this.isZooming = false), 400);
    }, 100);
  }

  // Swaps in the variant's media; the same media (e.g. another size of the same color) keeps the slide
  showVariant(variant) {
    const ids = this.variantMedia[variant.id];
    if (!ids) return;

    const nextIds = ids.map(String).filter(id => this.slidesHTML[id]);
    if (!nextIds.length || nextIds.join() === this.currentIds.join()) return;

    this.currentIds = nextIds;
    this.destroyFlickity();
    this.main.innerHTML = nextIds.map(id => this.slidesHTML[id]).join('');
    if (this.thumbsScroller) {
      this.thumbsScroller.innerHTML = nextIds.map(id => this.thumbsHTML[id] || '').join('');
    }
    this.mount();

    // Fresh zoom buttons need the lightbox wired up again
    try {
      if (window.theme?.Photoswipe) new window.theme.Photoswipe(this.root, this.root.dataset.sectionId || '');
    } catch (err) {
      console.warn('Error reinstating Photoswipe:', err);
    }
  }

  destroyFlickity() {
    const existingF = window.Flickity ? Flickity.data(this.main) : null;
    if (existingF) existingF.destroy();
    this.flickity = null;
  }

  // Indexes the current slides/thumbs and starts Flickity on slide 1
  mount() {
    const slides = Array.from(this.main.querySelectorAll(this.slideSelector));
    slides.forEach((slide, i) => {
      slide.setAttribute('data-index', i);
      slide.setAttribute('tabindex', i === 0 ? '0' : '-1');
      slide.classList.toggle('starting-slide', i === 0);
      slide.classList.toggle('is-first-selected', i === 0);
      slide.classList.remove('is-selected');
      slide.querySelectorAll('.photoswipe__image').forEach(img => img.setAttribute('data-index', i + 1));
    });

    if (this.thumbsScroller) {
      this.thumbsScroller.querySelectorAll(this.thumbItemSelector).forEach((t, i) => {
        t.setAttribute('data-index', i);
        const a = t.querySelector('[data-product-thumb]');
        if (a) {
          a.classList.toggle('is-active', i === 0);
          a.setAttribute('data-index', i);
        }
      });
    }

    if (!slides.length || !window.Flickity) return;

    this.flickity = new Flickity(this.main, {
      cellSelector: this.slideSelector,
      wrapAround: true,
      pageDots: true,
      prevNextButtons: true,
      adaptiveHeight: true,
      contain: true,
      cellAlign: 'center',
      dragThreshold: 3
    });

    // A tap or click on a photo opens it; staticClick doesn't fire at the end of a swipe or drag
    this.flickity.on('staticClick', (evt, pointer, cellElement) => this.openZoom(evt, cellElement));

    // Sync active thumb with slide change
    this.flickity.on('change', (index) => {
      if (this.thumbsScroller) {
        this.thumbsScroller.querySelectorAll('[data-product-thumb]').forEach(a => a.classList.remove('is-active'));
        const active = this.thumbsScroller.querySelector(`${this.thumbItemSelector}[data-index="${index}"] [data-product-thumb]`);
        if (active) active.classList.add('is-active');
        this.revealThumb(index);
      }
      this.main.querySelectorAll(this.slideSelector).forEach((s, i) => s.setAttribute('tabindex', i === index ? '0' : '-1'));
    });
    this.revealThumb(0);
  }

  // Scrolls a thumb column (or strip) so the active thumb is in view, without moving the page
  revealThumb(index) {
    const scroller = this.thumbsScroller;
    const item = scroller ? scroller.querySelector(`${this.thumbItemSelector}[data-index="${index}"]`) : null;
    if (!item) return;

    const box = scroller.getBoundingClientRect();
    const rect = item.getBoundingClientRect();
    if (scroller.scrollHeight > scroller.clientHeight) {
      scroller.scrollBy({ top: rect.top - box.top - (box.height - rect.height) / 2, behavior: 'smooth' });
    } else if (scroller.scrollWidth > scroller.clientWidth) {
      scroller.scrollBy({ left: rect.left - box.left - (box.width - rect.width) / 2, behavior: 'smooth' });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-images]').forEach((root) => {
    if (root.querySelector('[data-variant-media-map]')) new VariantMediaGallery(root);
  });
});
